import { validateCsvExecutionRow } from './universalImporter';
import { isIndexOrCfdInstrument } from '../../constants/indexInstruments';
import type { AssetClass as SessionAssetClass } from '../market/sessionValidator';

export type CsvRejectionDiagnostic = {
  index: number;
  row: any;
  accepted: boolean;
  severity: 'valid' | 'warning' | 'requires_review';
  reasons: string[];
  warnings?: string[];
  assetClass: SessionAssetClass;
  code?: string;
  symbol?: string;
  timestamp?: unknown;
};

export type CsvImportSummary = {
  sourceRowsProcessed: number;
  newTradesImported: number;
  duplicateTradesSkipped: number;
  duplicateExecutionsSkipped: number;
  invalidRowsSkipped: number;
  rejectedTradeLifecycles: number;
  tenantRestrictedTradesExcluded: number;
  sourceRowsByOutcome: {
    imported: number;
    duplicate: number;
    invalid: number;
    rejected: number;
    tenantRestricted: number;
    unclassified: number;
  };
};

export type CsvImportOutcome = {
  success: boolean;
  newTradesImported: number;
  duplicateTradesSkipped?: number;
  duplicateExecutionsSkipped?: number;
  invalidRowsSkipped?: number;
  rejectedTradeLifecycles?: number;
  tenantRestrictedTradesExcluded?: number;
  rowsRead?: number;
  message?: string;
};

export function isGenuineCsvIssueDiagnostic(diagnostic: Pick<CsvRejectionDiagnostic, 'accepted' | 'code'>): boolean {
  return diagnostic.accepted === false && (!diagnostic.code || [
    'CSV_VALIDATION_ERROR', 'INVALID_TIMESTAMP', 'AMBIGUOUS_TIMEZONE', 'INVALID_TIMEZONE',
  ].includes(diagnostic.code));
}

/** User-facing outcome copy. Counts are deliberately kept separate so
 * duplicates never appear as warnings and successful rows never appear as
 * issues. Detailed diagnostics remain available through the rejection log. */
export function formatCsvImportOutcome(outcome: CsvImportOutcome): string {
  const imported = countValue(outcome.newTradesImported);
  const duplicates = countValue(outcome.duplicateTradesSkipped);
  const warningCount = countValue(outcome.invalidRowsSkipped)
    + countValue(outcome.rejectedTradeLifecycles)
    + countValue(outcome.tenantRestrictedTradesExcluded);

  if (!outcome.success) {
    const failed = warningCount || countValue(outcome.rowsRead);
    return `Import Failed — ${failed} ${failed === 1 ? 'trade' : 'trades'} could not be imported`;
  }
  if (imported > 0 && warningCount > 0) {
    return `${imported} ${imported === 1 ? 'trade' : 'trades'} imported · ${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`;
  }
  if (warningCount > 0) {
    return `Import Failed — ${warningCount} ${warningCount === 1 ? 'trade' : 'trades'} could not be imported`;
  }
  if (imported > 0) {
    return `${imported} ${imported === 1 ? 'trade' : 'trades'} imported`;
  }
  if (duplicates > 0) {
    return `No new trades — ${duplicates} duplicates skipped`;
  }
  return outcome.message || 'Import Failed — 0 trades could not be imported';
}

function countValue(value: unknown): number {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue > 0 ? Math.floor(numberValue) : 0;
}

export function formatCsvImportSummary(summary: CsvImportSummary): string {
  const rows = summary.sourceRowsByOutcome;
  return [
    `Processed ${countValue(summary.sourceRowsProcessed)} source ${countValue(summary.sourceRowsProcessed) === 1 ? 'row' : 'rows'}`,
    `imported ${countValue(summary.newTradesImported)} new ${countValue(summary.newTradesImported) === 1 ? 'trade' : 'trades'}`,
    `skipped ${countValue(summary.duplicateTradesSkipped)} duplicate ${countValue(summary.duplicateTradesSkipped) === 1 ? 'trade' : 'trades'} (${countValue(summary.duplicateExecutionsSkipped)} ${countValue(summary.duplicateExecutionsSkipped) === 1 ? 'execution' : 'executions'})`,
    `skipped ${countValue(summary.invalidRowsSkipped)} invalid ${countValue(summary.invalidRowsSkipped) === 1 ? 'row' : 'rows'}`,
    `rejected ${countValue(summary.rejectedTradeLifecycles)} trade ${countValue(summary.rejectedTradeLifecycles) === 1 ? 'lifecycle' : 'lifecycles'}`,
    `excluded ${countValue(summary.tenantRestrictedTradesExcluded)} tenant-restricted ${countValue(summary.tenantRestrictedTradesExcluded) === 1 ? 'trade' : 'trades'}`,
    `source-row outcomes: ${rows.imported} imported, ${rows.duplicate} duplicate, ${rows.invalid} invalid, ${rows.rejected} rejected, ${rows.tenantRestricted} tenant-restricted, ${rows.unclassified} unclassified`,
  ].join('; ') + '.';
}

/** Concise import outcome copy for the success toast; detailed diagnostics stay in the summary/rejection log. */
export function formatCsvImportToast(summary: CsvImportSummary): string {
  const imported = countValue(summary.newTradesImported);
  const duplicates = countValue(summary.sourceRowsByOutcome.duplicate);
  const skippedRows = duplicates
    + countValue(summary.sourceRowsByOutcome.invalid)
    + countValue(summary.sourceRowsByOutcome.rejected)
    + countValue(summary.sourceRowsByOutcome.tenantRestricted);

  if (imported > 0 && skippedRows === 0) {
    return `Import complete — ${imported} ${imported === 1 ? 'trade' : 'trades'} imported successfully.`;
  }
  if (imported === 0 && duplicates > 0 && skippedRows === duplicates) {
    return `No new trades imported. ${duplicates} duplicate${duplicates === 1 ? '' : 's'} skipped.`;
  }
  if (imported > 0 && skippedRows > 0) {
    return `${imported} ${imported === 1 ? 'trade' : 'trades'} imported. ${skippedRows} ${skippedRows === 1 ? 'row' : 'rows'} skipped.`;
  }
  return formatCsvImportSummary(summary);
}

function getSourceIdentityValues(value: any): string[] {
  const values: unknown[] = [];
  if (Array.isArray(value?.sourceOrderIds)) values.push(...value.sourceOrderIds);
  if (Array.isArray(value?.orderIds)) values.push(...value.orderIds);
  const executions = Array.isArray(value?.executionsList)
    ? value.executionsList
    : Array.isArray(value?.executions) ? value.executions : [];
  for (const execution of executions) {
    values.push(execution?.orderId, execution?.order_id, execution?.id, execution?.executionId, execution?.execId);
  }
  return [...new Set(values.map((item) => String(item ?? '').trim()).filter(Boolean))];
}

function getSourceRowIdentityValues(row: any): string[] {
  const identityKeys = new Set(['tradeid', 'orderid', 'executionid', 'execid', 'id']);
  return Object.entries(row || {})
    .filter(([key]) => identityKeys.has(String(key).toLowerCase().replace(/[^a-z0-9]/g, '')))
    .map(([, value]) => String(value ?? '').trim())
    .filter(Boolean);
}

export function getTradeSourceRowOrdinals(trade: any, sourceRows: any[] = []): number[] {
  const executions = Array.isArray(trade?.executionsList)
    ? trade.executionsList
    : Array.isArray(trade?.executions) ? trade.executions : [];
  const evidenceOrdinals = [...new Set(executions.flatMap((execution: any) => {
    const ordinal = Number(execution?.metadata?.orderingEvidence?.sourceRowOrdinal ?? execution?.sourceRowOrdinal);
    return Number.isInteger(ordinal) && ordinal >= 0 ? [ordinal + 1] : [];
  }))].sort((a, b) => a - b);
  if (evidenceOrdinals.length || !sourceRows.length) return evidenceOrdinals;

  const identities = new Set(getSourceIdentityValues(trade));
  if (!identities.size) return [];
  return collectMeaningfulCsvRows(sourceRows)
    .filter(({ row }) => {
      const rowIdentities = getSourceRowIdentityValues(row);
      return rowIdentities.some((identity) => identities.has(identity));
    })
    .map(({ index }) => index + 1);
}

export function buildCsvImportSummary(input: {
  rows: any[];
  newTradesImported: number;
  duplicateTradesSkipped: number;
  duplicateExecutionsSkipped: number;
  invalidRowsSkipped: number;
  rejectedTradeLifecycles: number;
  tenantRestrictedTradesExcluded: number;
  importedTrades?: any[];
  rowDiagnostics?: CsvRejectionDiagnostic[];
  duplicateTradeDiagnostics?: any[];
  rejectedLifecycleDiagnostics?: any[];
  tenantRestrictedDiagnostics?: any[];
}): CsvImportSummary {
  const rowCategories = new Map<number, 'imported' | 'duplicate' | 'invalid' | 'rejected' | 'tenantRestricted'>();
  const mark = (ordinals: number[], category: 'imported' | 'duplicate' | 'invalid' | 'rejected' | 'tenantRestricted') => {
    for (const ordinal of ordinals) {
      if (!Number.isInteger(ordinal) || ordinal < 1) continue;
      // A source row gets one disposition, even if malformed upstream diagnostics overlap.
      if (rowCategories.has(ordinal)) continue;
      rowCategories.set(ordinal, category);
    }
  };

  const sourceDiagnostics = input.rowDiagnostics || buildCsvRejectionDiagnostics(input.rows || []);
  for (const diagnostic of sourceDiagnostics) {
    if (!diagnostic.accepted && diagnostic.severity === 'warning') mark([diagnostic.index], 'invalid');
  }
  for (const diagnostic of input.rejectedLifecycleDiagnostics || []) mark(diagnostic.rowOrdinals || [], 'rejected');
  for (const diagnostic of input.tenantRestrictedDiagnostics || []) mark(diagnostic.rowOrdinals || [], 'tenantRestricted');
  for (const diagnostic of input.duplicateTradeDiagnostics || []) {
    mark(diagnostic.rowOrdinals?.length ? diagnostic.rowOrdinals : getTradeSourceRowOrdinals(diagnostic, input.rows || []), 'duplicate');
  }
  for (const diagnostic of input.rejectedLifecycleDiagnostics || []) {
    mark(diagnostic.rowOrdinals?.length ? diagnostic.rowOrdinals : getTradeSourceRowOrdinals(diagnostic, input.rows || []), 'rejected');
  }
  for (const diagnostic of input.tenantRestrictedDiagnostics || []) {
    mark(diagnostic.rowOrdinals?.length ? diagnostic.rowOrdinals : getTradeSourceRowOrdinals(diagnostic, input.rows || []), 'tenantRestricted');
  }
  for (const trade of input.importedTrades || []) mark(getTradeSourceRowOrdinals(trade, input.rows || []), 'imported');

  const processedRowOrdinals = collectMeaningfulCsvRows(input.rows || []).map(({ index }) => index + 1);
  const processed = processedRowOrdinals.length;
  const sourceRowsByOutcome = {
    imported: 0,
    duplicate: 0,
    invalid: 0,
    rejected: 0,
    tenantRestricted: 0,
    unclassified: 0,
  };
  for (const ordinal of processedRowOrdinals) {
    const category = rowCategories.get(ordinal);
    if (category) sourceRowsByOutcome[category] += 1;
    else sourceRowsByOutcome.unclassified += 1;
  }

  return {
    sourceRowsProcessed: processed,
    newTradesImported: countValue(input.newTradesImported),
    duplicateTradesSkipped: countValue(input.duplicateTradesSkipped),
    duplicateExecutionsSkipped: countValue(input.duplicateExecutionsSkipped),
    invalidRowsSkipped: countValue(input.invalidRowsSkipped),
    rejectedTradeLifecycles: countValue(input.rejectedTradeLifecycles),
    tenantRestrictedTradesExcluded: countValue(input.tenantRestrictedTradesExcluded),
    sourceRowsByOutcome,
  };
}

type CsvRow = Record<string, any>;

function isBlankValue(value: unknown): boolean {
  if (value == null) return true;
  if (Array.isArray(value)) return value.every((item) => isBlankValue(item));
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every((item) => isBlankValue(item));
  return String(value).trim() === '';
}

export function isBlankCsvRow(row: unknown): boolean {
  if (!row || typeof row !== 'object') return true;
  return Object.values(row as CsvRow).every((value) => isBlankValue(value));
}

export function collectMeaningfulCsvRows(rows: any[]): Array<{ row: any; index: number }> {
  return (Array.isArray(rows) ? rows : [])
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => !isBlankCsvRow(row));
}

function normalizeAssetClassHint(value: unknown): SessionAssetClass | null {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) return null;

  if (['STK', 'STOCK', 'STOCKS', 'EQUITY', 'EQUITIES', 'SHARE', 'SHARES'].includes(normalized)) return 'STK';
  if (['INDEX_CFD', 'CASH_INDEX', 'INDEX', 'INDICES'].includes(normalized)) return 'CASH';
  if (['FUT', 'FUTURE', 'FUTURES', 'DERIVATIVE', 'DERIVATIVES'].includes(normalized)) return 'FUT';
  if (['CASH', 'FOREX', 'FX', 'CURRENCY', 'CURRENCIES'].includes(normalized)) return 'CASH';
  if (['CRYPTO', 'CRYPTOCURRENCY', 'CRYPTOCURRENCIES', 'DIGITAL_ASSET', 'DIGITAL_ASSETS'].includes(normalized)) return 'CRYPTO';

  return null;
}

function inferSessionAssetClassFromSymbol(symbol: string): SessionAssetClass {
  const normalized = String(symbol || '').trim().toUpperCase();
  const compact = normalized.replace(/[\s._-]/g, '');
  const noVenue = compact.includes(':') ? (compact.split(':').pop() || compact) : compact;
  const isFxPair = /^[A-Z]{3}\/?[A-Z]{3}$/.test(noVenue);
  const slashParts = noVenue.split('/').filter(Boolean);
  const normalizedContract = !isFxPair && slashParts.length > 0 ? slashParts[0] : noVenue;

  const futuresRoots = new Set(['ES', 'MES', 'NQ', 'MNQ', 'RTY', 'M2K', 'YM', 'MYM', 'CL', 'GC', 'SI', 'HG', 'NG', 'ZB', 'ZN', 'ZF', 'ZT', '6A', '6B', '6C', '6E', '6J', '6N', '6S']);
  const cryptoRoots = new Set(['BTC', 'ETH', 'LTC', 'XRP', 'DOGE', 'SOL', 'ADA', 'BNB']);
  const fxCodes = new Set(['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'NZD', 'SEK', 'NOK', 'DKK', 'HKD', 'SGD', 'CNH', 'CNY', 'MXN', 'ZAR', 'TRY', 'PLN', 'HUF', 'CZK', 'ILS', 'AED', 'SAR']);

  if (futuresRoots.has(normalizedContract)) return 'FUT';
  if (/^(ES|MES|NQ|MNQ|RTY|M2K|YM|MYM|CL|GC|SI|HG|NG|ZB|ZN|ZF|ZT|6A|6B|6C|6E|6J|6N|6S)[FGHJKMNQUVXZ]\d{1,2}$/.test(normalizedContract)) return 'FUT';
  if (/^[A-Z]{1,6}[FGHJKMNQUVXZ]\d{1,4}$/.test(normalizedContract)) return 'FUT';
  if (/^\d[A-Z]([FGHJKMNQUVXZ]\d{1,2})?$/.test(normalizedContract)) return 'FUT';
  if (isIndexOrCfdInstrument(normalized)) return 'CASH';
  if (cryptoRoots.has(normalizedContract) || /^(BTC|ETH|LTC|XRP|DOGE|SOL|ADA|BNB)\b/.test(normalizedContract)) return 'CRYPTO';

  if (isFxPair) return 'CASH';

  if (/^[A-Z]{6}$/.test(normalizedContract)) {
    const base = normalizedContract.slice(0, 3);
    const quote = normalizedContract.slice(3);
    if (cryptoRoots.has(base) || cryptoRoots.has(quote)) return 'CRYPTO';
    if (fxCodes.has(base) && fxCodes.has(quote)) return 'CASH';
    if (quote === 'USD') return 'CASH';
  }

  return 'STK';
}

export function inferCsvRowSessionAssetClass(row: CsvRow): SessionAssetClass {
  const explicitHintFields = [
    row?.assetClass,
    row?.asset_class,
    row?.assetclass,
    row?.secType,
    row?.sec_type,
    row?.securityType,
    row?.security_type,
    row?.instrumentType,
    row?.instrument_type,
    row?.marketType,
    row?.market_type,
    row?.market,
  ];

  for (const hint of explicitHintFields) {
    const normalized = normalizeAssetClassHint(hint);
    if (normalized) return normalized;
  }

  const symbol = String(row?.symbol ?? row?.ticker ?? row?.instrument ?? row?.product ?? row?.contract ?? '').trim();
  return inferSessionAssetClassFromSymbol(symbol);
}

function buildRowReasons(row: CsvRow, index: number, timeZone?: string) {
  return validateCsvExecutionRow(row, index, { timeZone });
}

export function buildCsvRowRejectionDiagnostic(row: CsvRow, index: number, options?: { timeZone?: string }): CsvRejectionDiagnostic | null {
  if (isBlankCsvRow(row)) return null;

  const { reasons, assetClass, warnings = [], code } = buildRowReasons(row, index, options?.timeZone);
  const accepted = reasons.length === 0;
  return {
    index: index + 1,
    row,
    accepted,
    severity: !accepted ? 'warning' : warnings.length ? 'warning' : 'valid',
    reasons,
    ...(warnings.length ? { warnings } : {}),
    assetClass,
    ...(code ? { code } : {}),
    symbol: String(row?.symbol ?? row?.ticker ?? row?.instrument ?? '').trim() || undefined,
    timestamp: row?.date ?? row?.datetime ?? row?.timestamp ?? row?.time,
  };
}

export function buildCsvRejectionDiagnostics(rows: any[], options?: { timeZone?: string }): CsvRejectionDiagnostic[] {
  return collectMeaningfulCsvRows(rows)
    .map(({ row, index }) => buildCsvRowRejectionDiagnostic(row, index, options))
    .filter((diagnostic): diagnostic is CsvRejectionDiagnostic => Boolean(diagnostic));
}

export function applyImportOverlapDiagnostics<T extends CsvRejectionDiagnostic>(
  rowDiagnostics: T[],
  overlapDiagnostics: Array<{
    code?: string;
    symbol?: string;
    assetClass?: string;
    executionCount?: number;
    reasons?: string[];
    rowOrdinals?: number[];
    sourceOrderIds?: string[];
  }>,
  sourceRows: any[] = [],
): T[] {
  const next = rowDiagnostics.map((diagnostic) => ({ ...diagnostic, reasons: [...diagnostic.reasons] }));
  const byOrdinal = new Map(next.map((diagnostic) => [diagnostic.index, diagnostic]));

  for (const overlap of Array.isArray(overlapDiagnostics) ? overlapDiagnostics : []) {
    const reason = overlap.reasons?.[0] || 'This lifecycle overlaps existing executions and requires review before import.';
    const ordinals = [...new Set((overlap.rowOrdinals?.length ? overlap.rowOrdinals : getTradeSourceRowOrdinals(overlap, sourceRows))
      .filter((ordinal) => Number.isInteger(ordinal) && ordinal > 0))];
    if (ordinals.length) {
      for (const ordinal of ordinals) {
        const diagnostic = byOrdinal.get(ordinal);
        if (!diagnostic) continue;
        diagnostic.accepted = false;
        diagnostic.severity = 'requires_review';
        diagnostic.code = overlap.code || 'PARTIAL_EXECUTION_OVERLAP_REQUIRES_REVIEW';
        if (!diagnostic.reasons.includes(reason)) diagnostic.reasons.push(reason);
      }
      continue;
    }

    const assetClass = normalizeAssetClassHint(overlap.assetClass) || inferSessionAssetClassFromSymbol(overlap.symbol || '');
    next.push({
      index: next.length ? Math.max(...next.map((item) => item.index)) + 1 : 1,
      row: { symbol: overlap.symbol || 'Unknown instrument', assetClass: overlap.assetClass || 'UNKNOWN' },
      accepted: false,
      severity: 'requires_review',
      reasons: [reason],
      assetClass,
      code: overlap.code || 'PARTIAL_EXECUTION_OVERLAP_REQUIRES_REVIEW',
    } as T);
  }

  return next;
}

export type CsvImportSummaryCounts = {
  meaningfulRowCount: number;
  importedExecutionCount: number;
  duplicateExecutionCount: number;
  skippedInvalidCount: number;
  rejectedCount: number;
};

function toNonNegativeInteger(value: unknown): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return 0;
  return Math.floor(numberValue);
}

export function reconcileCsvImportSummaryCounts(
  rows: any[],
  importedExecutionCount: number,
  rejectedCount: number,
  duplicateExecutionCount = 0,
  options?: { timeZone?: string },
): CsvImportSummaryCounts {
  const meaningfulRowCount = collectMeaningfulCsvRows(rows).length;
  const diagnostics = buildCsvRejectionDiagnostics(rows, options);
  const normalizedImportedExecutionCount = toNonNegativeInteger(importedExecutionCount);
  const normalizedDuplicateExecutionCount = toNonNegativeInteger(duplicateExecutionCount);
  const skippedInvalidCount = diagnostics.filter((diagnostic) => !diagnostic.accepted).length;

  return {
    meaningfulRowCount,
    importedExecutionCount: normalizedImportedExecutionCount,
    duplicateExecutionCount: normalizedDuplicateExecutionCount,
    skippedInvalidCount,
    rejectedCount: toNonNegativeInteger(rejectedCount),
  };
}
